import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { useTheme, useCompany } from "@/lib/theme";
import { PageHeader, SectionTitle } from "@/components/common";
import { roleLabel } from "@/lib/constants";
import { NAV_MODULES, defaultModuleAccess, mergeModuleAccess } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sun, Moon, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field } from "./Employees";

export default function Settings() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { company, refresh: refreshCompany, logoUrl } = useCompany();
  const isSA = hasRole(user, "super_admin");

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" description="Personal preferences and (for Super Admins) system configuration." />

      <Tabs defaultValue="preferences">
        <TabsList>
          <TabsTrigger value="preferences" data-testid="settings-tab-preferences">Preferences</TabsTrigger>
          <TabsTrigger value="password" data-testid="settings-tab-password">Change Password</TabsTrigger>
          {isSA && <TabsTrigger value="branding" data-testid="settings-tab-branding">Company Branding</TabsTrigger>}
          {isSA && <TabsTrigger value="attendance" data-testid="settings-tab-attendance">Attendance</TabsTrigger>}
          {isSA && <TabsTrigger value="payroll" data-testid="settings-tab-payroll">Payroll</TabsTrigger>}
          {isSA && <TabsTrigger value="projects" data-testid="settings-tab-projects">Projects</TabsTrigger>}
          {isSA && <TabsTrigger value="access" data-testid="settings-tab-access">Access Control</TabsTrigger>}
          {isSA && <TabsTrigger value="payslip" data-testid="settings-tab-payslip">Salary Slip</TabsTrigger>}
          {isSA && <TabsTrigger value="whatsapp" data-testid="settings-tab-whatsapp">WhatsApp</TabsTrigger>}
          {isSA && <TabsTrigger value="danger" data-testid="settings-tab-danger">Danger Zone</TabsTrigger>}
        </TabsList>

        {/* PREFERENCES (theme) */}
        <TabsContent value="preferences" className="mt-4 space-y-4">
          <MyProfileCard />
          <Card className="p-6 max-w-2xl">
            <h3 className="font-semibold mb-1">Appearance</h3>
            <p className="text-sm opacity-70 mb-4">Choose between light and dark mode.</p>
            <div className="flex items-center gap-3">
              <Button variant={theme === "light" ? "default" : "outline"} onClick={() => theme !== "light" && toggle()} className={theme === "light" ? "btn-brand" : ""} data-testid="theme-light-btn">
                <Sun size={16} className="mr-2" /> Light
              </Button>
              <Button variant={theme === "dark" ? "default" : "outline"} onClick={() => theme !== "dark" && toggle()} className={theme === "dark" ? "btn-brand" : ""} data-testid="theme-dark-btn">
                <Moon size={16} className="mr-2" /> Dark
              </Button>
              <span className="text-sm opacity-60 ml-3">Currently: <b className="capitalize">{theme}</b></span>
            </div>
          </Card>
        </TabsContent>

        {/* CHANGE PASSWORD */}
        <TabsContent value="password" className="mt-4">
          <ChangePasswordCard />
        </TabsContent>

        {/* BRANDING (Super Admin) */}
        {isSA && (
          <TabsContent value="branding" className="mt-4">
            <BrandingCard company={company} refresh={refreshCompany} logoUrl={logoUrl} />
          </TabsContent>
        )}

        {/* WHATSAPP (Super Admin) */}
        {isSA && (
          <TabsContent value="attendance" className="mt-4"><AttendanceSettingsCard /></TabsContent>
        )}
        {isSA && (
          <TabsContent value="payroll" className="mt-4"><PayrollCalendarCard /></TabsContent>
        )}
        {isSA && (
          <TabsContent value="projects" className="mt-4"><ProjectsConfigCard /></TabsContent>
        )}
        {isSA && (
          <TabsContent value="access" className="mt-4"><AccessControlCard /></TabsContent>
        )}
        {isSA && (
          <TabsContent value="payslip" className="mt-4"><PayslipSettingsCard /></TabsContent>
        )}
        {isSA && (
          <TabsContent value="whatsapp" className="mt-4"><WhatsAppCard /></TabsContent>
        )}

        {/* DANGER ZONE (Super Admin) */}
        {isSA && (
          <TabsContent value="danger" className="mt-4">
            <DangerZoneCard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-semibold mb-1">Change Password</h3>
      <p className="text-sm opacity-70 mb-4">Change your account password securely.</p>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Current Password"><PasswordInput data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} /></Field>
        <Field label="New Password"><PasswordInput data-testid="cp-new" value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <Field label="Confirm New Password"><PasswordInput data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <div className="pt-2">
          <Button type="submit" disabled={loading} className="btn-brand" data-testid="cp-submit">
            {loading ? "Updating…" : "Update Password"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function BrandingCard({ company, refresh, logoUrl }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (company) setForm({ ...company }); }, [company]);

  const onLogoChange = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "branding");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_file_id: r.data.id }));
      toast.success("Logo uploaded — click Save to apply.");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      await api.put("/config/company", form);
      toast.success("Company branding updated");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="p-6 max-w-2xl">
      <h3 className="font-semibold mb-1">Company Branding</h3>
      <p className="text-sm opacity-70 mb-4">Customise the logo, name and contact details shown across the platform.</p>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-20 h-20 rounded-lg border flex items-center justify-center bg-white overflow-hidden" style={{ borderColor: "hsl(var(--border))" }}>
          {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" /> : <ImageIcon size={28} className="opacity-30" />}
        </div>
        <div>
          <Label className="text-xs">Company Logo</Label>
          <div className="mt-1.5 flex items-center gap-2">
            <Input data-testid="branding-logo-input" type="file" accept="image/*" onChange={(e) => onLogoChange(e.target.files?.[0])} />
          </div>
          <p className="text-xs opacity-60 mt-1">PNG or JPG, square works best. Max 10MB.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company Name"><Input data-testid="branding-name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
        <Field label="Tagline"><Input data-testid="branding-tagline" value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field>
        <Field label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
        <Field label="Support Email"><Input value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} /></Field>
        <Field label="Support Phone"><Input value={form.support_phone || ""} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} /></Field>
        <Field label="Primary Color"><Input type="text" value={form.primary_color || "#fd7e14"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} /></Field>
        <div className="sm:col-span-2"><Field label="Address"><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
      </div>

      <div className="mt-6 text-right">
        <Button onClick={save} className="btn-brand" data-testid="branding-save">Save Branding</Button>
      </div>
    </Card>
  );
}

function WhatsAppCard() {
  const [cfg, setCfg] = useState({ provider: "", api_key: "", api_secret: "", business_number: "", template_activation: "", template_otp: "", template_task: "", template_approval: "", enabled: false });
  useEffect(() => { api.get("/config/whatsapp").then((r) => setCfg((c) => ({ ...c, ...r.data }))); }, []);
  const save = async () => {
    try { await api.put("/config/whatsapp", cfg); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold">WhatsApp Business API</h3>
          <p className="text-xs opacity-60">Used for activation links, OTPs, task & approval notifications.</p>
        </div>
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="whatsapp-enable-switch" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Provider"><Input value={cfg.provider || ""} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} placeholder="twilio / meta / interakt" /></Field>
        <Field label="Business Number"><Input value={cfg.business_number || ""} onChange={(e) => setCfg({ ...cfg, business_number: e.target.value })} /></Field>
        <Field label="API Key"><Input value={cfg.api_key || ""} onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })} /></Field>
        <Field label="API Secret"><Input type="password" value={cfg.api_secret || ""} onChange={(e) => setCfg({ ...cfg, api_secret: e.target.value })} /></Field>
        <Field label="Activation Template"><Input value={cfg.template_activation || ""} onChange={(e) => setCfg({ ...cfg, template_activation: e.target.value })} /></Field>
        <Field label="OTP Template"><Input value={cfg.template_otp || ""} onChange={(e) => setCfg({ ...cfg, template_otp: e.target.value })} /></Field>
        <Field label="Task Template"><Input value={cfg.template_task || ""} onChange={(e) => setCfg({ ...cfg, template_task: e.target.value })} /></Field>
        <Field label="Approval Template"><Input value={cfg.template_approval || ""} onChange={(e) => setCfg({ ...cfg, template_approval: e.target.value })} /></Field>
      </div>
      <div className="mt-6 text-right">
        <Button onClick={save} className="btn-brand" data-testid="settings-save">Save Configuration</Button>
      </div>
      <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40">
        <b>Note:</b> WhatsApp send is currently MOCKED — activation links and OTPs are logged server-side and surfaced in the UI. Enable + provide credentials when ready to go live.
      </div>
    </Card>
  );
}


function DangerZoneCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const reset = async () => {
    const confirmText = window.prompt("This will permanently delete ALL accounts, opportunities, tasks, attendance, expense/travel claims, inventory, products, files & audit logs. Super admin users, departments, designations and payroll config are kept.\n\nType DELETE to confirm:");
    if ((confirmText || "").trim() !== "DELETE") return;
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-demo-data");
      setResult(r.data?.deleted || {});
      toast.success("All test data deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-6 max-w-2xl border-red-200">
      <h3 className="font-semibold mb-1 text-red-700 dark:text-red-400">Reset All Test Data</h3>
      <p className="text-sm opacity-70 mb-4">
        Wipes accounts, opportunities, tasks, travel, expenses, approvals, attendance, inventory, products, OEMs, files, audit logs and notifications. Keeps super admins, departments, designations, payroll components, TDS/PT config and company branding.
      </p>
      <Button onClick={reset} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reset-demo-btn">
        {busy ? "Resetting…" : "Reset all test data"}
      </Button>
      {result && (
        <pre className="mt-4 text-xs bg-slate-50 dark:bg-slate-900 border rounded p-3 overflow-auto" style={{ borderColor: "hsl(var(--border))" }} data-testid="reset-result">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </Card>
  );
}

function AppSettingsCard({ title, description, keys, saveTestId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/app-settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Card className="p-6 max-w-2xl">Loading…</Card>;
  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      keys.forEach((k) => { patch[k.key] = cfg[k.key]; });
      await api.put("/app-settings", patch);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 max-w-2xl">
      <h3 className="font-semibold mb-1">{title}</h3>
      {description && <p className="text-sm opacity-70 mb-4">{description}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {keys.map((k) => (
          <div key={k.key} className={k.wide ? "sm:col-span-2" : ""}>
            {k.type === "switch" ? (
              <div className="flex items-center justify-between rounded-md border p-3" style={{ borderColor: "hsl(var(--border))" }}>
                <div>
                  <div className="text-sm font-medium">{k.label}</div>
                  {k.help && <div className="text-xs opacity-60 mt-0.5">{k.help}</div>}
                </div>
                <Switch checked={!!cfg[k.key]} onCheckedChange={(v) => setCfg({ ...cfg, [k.key]: v })} data-testid={`app-setting-${k.key}`} />
              </div>
            ) : (
              <Field label={k.label}>
                <Input
                  type={k.type || "number"}
                  step={k.step}
                  value={cfg[k.key] ?? ""}
                  onChange={(e) => setCfg({ ...cfg, [k.key]: k.type === "text" ? e.target.value : Number(e.target.value) })}
                  data-testid={`app-setting-${k.key}`}
                  placeholder={k.placeholder || ""}
                />
                {k.help && <div className="text-xs opacity-60 mt-1">{k.help}</div>}
              </Field>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 text-right">
        <Button className="btn-brand" onClick={save} disabled={saving} data-testid={saveTestId}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </Card>
  );
}

function AttendanceSettingsCard() {
  return (
    <AppSettingsCard
      title="Attendance Management"
      description="Control how employees regularise their attendance and whether they must capture a live selfie while punching."
      saveTestId="attendance-settings-save"
      keys={[
        { key: "attendance_regularization_enabled", label: "Enable Regularization", type: "switch", help: "If enabled, employees can raise correction requests for missed / incorrect punches." },
        { key: "attendance_regularization_limit_per_month", label: "Regularization Limit (per month)", help: "Maximum number of regularization requests allowed per Employee each calendar month." },
        { key: "attendance_photo_punch_enabled", label: "Enable Photo Punch", type: "switch", wide: true, help: "When enabled, Employees must capture a photo while punching attendance. If disabled photo is not needed." },
      ]}
    />
  );
}

function PayrollCalendarCard() {
  return (
    <AppSettingsCard
      title="Payroll Calendar"
      description="Full-day, half-day and absent classification used for attendance-linked payroll."
      saveTestId="payroll-cal-save"
      keys={[
        { key: "full_day_hours", label: "Full Day Working Hours", step: 0.25, help: "e.g. 8 = 8 hours of work counts as full day." },
        { key: "half_day_hours", label: "Half Day Working Hours", step: 0.25, help: "e.g. 4 = 4 hours or more counts as half day." },
        { key: "mark_absent_if_below_half_day", label: "Mark Absent if worked less than Half Day", type: "switch", wide: true, help: "If enabled, employees who work below the half-day threshold will be marked Absent." },
      ]}
    />
  );
}

function PayslipSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/app-settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Card className="p-6 max-w-2xl">Loading…</Card>;
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "payslip_logo");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg({ ...cfg, payslip_logo_file_id: r.data.id });
      toast.success("Logo uploaded — click Save to apply");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        payslip_company_name: cfg.payslip_company_name,
        payslip_company_address: cfg.payslip_company_address,
        payslip_company_contact: cfg.payslip_company_contact,
        payslip_company_website: cfg.payslip_company_website,
        payslip_logo_file_id: cfg.payslip_logo_file_id,
        payslip_auto_release: !!cfg.payslip_auto_release,
      };
      await api.put("/app-settings", patch);
      toast.success("Saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 max-w-2xl">
      <h3 className="font-semibold mb-1">Salary Slip</h3>
      <p className="text-sm opacity-70 mb-4">Company info used on the payslip PDF header, plus automatic monthly release schedule.</p>

      <div className="flex items-center gap-4 mb-5">
        <div className="w-20 h-20 rounded-lg border flex items-center justify-center overflow-hidden" style={{ borderColor: "hsl(var(--border))" }}>
          {cfg.payslip_logo_file_id ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/files/${cfg.payslip_logo_file_id}/download?token=${encodeURIComponent(localStorage.getItem("smipl_token") || "")}`} alt="Payslip logo" className="w-full h-full object-contain" />
          ) : <ImageIcon size={28} className="opacity-30" />}
        </div>
        <div className="flex-1">
          <Label className="text-xs">Payslip Logo</Label>
          <Input type="file" accept="image/*" data-testid="payslip-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} className="mt-1.5" />
          {uploading && <p className="text-xs opacity-60 mt-1">Uploading…</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company Name"><Input value={cfg.payslip_company_name || ""} onChange={(e) => setCfg({ ...cfg, payslip_company_name: e.target.value })} data-testid="payslip-name" /></Field>
        <Field label="Contact"><Input value={cfg.payslip_company_contact || ""} onChange={(e) => setCfg({ ...cfg, payslip_company_contact: e.target.value })} data-testid="payslip-contact" placeholder="phone / email" /></Field>
        <Field label="Website"><Input value={cfg.payslip_company_website || ""} onChange={(e) => setCfg({ ...cfg, payslip_company_website: e.target.value })} data-testid="payslip-website" /></Field>
        <div className="sm:col-span-2"><Field label="Address"><Textarea value={cfg.payslip_company_address || ""} onChange={(e) => setCfg({ ...cfg, payslip_company_address: e.target.value })} data-testid="payslip-address" /></Field></div>

        <div className="sm:col-span-2">
          <div className="flex items-center justify-between rounded-md border p-3" style={{ borderColor: "hsl(var(--border))" }}>
            <div>
              <div className="text-sm font-medium">Automatic monthly release</div>
              <div className="text-xs opacity-60 mt-0.5">When enabled, payslips are automatically emailed / made available to employees on the 1st of each month. Otherwise, Finance Manager must release them manually.</div>
            </div>
            <Switch checked={!!cfg.payslip_auto_release} onCheckedChange={(v) => setCfg({ ...cfg, payslip_auto_release: v })} data-testid="payslip-auto" />
          </div>
        </div>
      </div>

      <div className="mt-6 text-right">
        <Button className="btn-brand" onClick={save} disabled={saving} data-testid="payslip-save">{saving ? "Saving…" : "Save Salary Slip"}</Button>
      </div>
    </Card>
  );
}


function MyProfileCard() {
  const { user, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  if (!user) return null;
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "profile");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/me/profile-picture", { profile_picture_file_id: r.data.id });
      await refreshUser();
      toast.success("Profile picture updated");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const clearPic = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    try {
      await api.put("/auth/me/profile-picture", { profile_picture_file_id: null });
      await refreshUser();
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };
  return (
    <Card className="p-6 max-w-2xl">
      <h3 className="font-semibold mb-1">My Profile</h3>
      <p className="text-sm opacity-70 mb-4">This picture is shown to you and your colleagues across the platform.</p>
      <div className="flex items-center gap-5">
        <div className="w-24 h-24 rounded-full overflow-hidden border" style={{ borderColor: "hsl(var(--border))" }}>
          {user.profile_picture_file_id ? (
            <img src={fileUrl(user.profile_picture_file_id)} alt={user.full_name} className="w-full h-full object-cover" data-testid="my-profile-pic" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#fd7e14] text-white text-3xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              {(user.full_name || "?")[0]}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">{user.full_name}</div>
          <div className="text-xs opacity-60 mb-2">{user.mobile} • <span className="capitalize">{roleLabel(user.role)}</span></div>
          <div className="flex items-center gap-2">
            <Input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} data-testid="my-profile-pic-input" className="max-w-xs" />
            {user.profile_picture_file_id && <Button size="sm" variant="outline" className="text-red-600" onClick={clearPic} data-testid="my-profile-pic-clear">Remove</Button>}
          </div>
          {uploading && <p className="text-xs opacity-60 mt-1">Uploading…</p>}
        </div>
      </div>
    </Card>
  );
}


const ALL_ROLES = ["super_admin", "hr", "sales_manager", "department_manager", "employee", "finance_team"];

function ProjectsConfigCard() {
  const [cfg, setCfg] = useState({ statuses: "", priorities: "", milestone_statuses: "", visible_roles: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/app-settings").then((r) => {
      const c = r.data?.projects_config || {};
      setCfg({
        statuses: (c.statuses || ["draft", "planning", "active", "on_hold", "completed", "cancelled"]).join(", "),
        priorities: (c.priorities || ["low", "medium", "high", "critical"]).join(", "),
        milestone_statuses: (c.milestone_statuses || ["pending", "in_progress", "completed", "delayed"]).join(", "),
        visible_roles: (c.visible_roles || ALL_ROLES).filter((r) => ALL_ROLES.includes(r)),
      });
    }).catch(() => {});
  }, []);
  const toggleRole = (r) => {
    setCfg((c) => ({ ...c, visible_roles: c.visible_roles.includes(r) ? c.visible_roles.filter((x) => x !== r) : [...c.visible_roles, r] }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        projects_config: {
          statuses: cfg.statuses.split(",").map((s) => s.trim().toLowerCase().replace(/\s+/g, "_")).filter(Boolean),
          priorities: cfg.priorities.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
          milestone_statuses: cfg.milestone_statuses.split(",").map((s) => s.trim().toLowerCase().replace(/\s+/g, "_")).filter(Boolean),
          visible_roles: cfg.visible_roles.filter((r) => ALL_ROLES.includes(r)),
        },
      };
      await api.put("/app-settings", payload);
      toast.success("Projects config saved");
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <Card className="p-5 space-y-4">
      <SectionTitle title="Projects Module Configuration" description="Control which roles can access the Projects module, and edit the project Status / Priority / Milestone dropdown values." />
      <div>
        <Label>Roles that see the Projects module</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {ALL_ROLES.map((r) => (
            <label key={r} className={`px-3 py-1.5 rounded-md border text-sm cursor-pointer capitalize ${cfg.visible_roles.includes(r) ? "bg-orange-500 text-white border-orange-500" : "bg-white dark:bg-slate-800"}`}
                   data-testid={`proj-role-${r}`}>
              <input type="checkbox" className="hidden" checked={cfg.visible_roles.includes(r)} onChange={() => toggleRole(r)} />
              {roleLabel(r)}
            </label>
          ))}
        </div>
      </div>
      <div>
        <Label>Project Statuses (comma-separated)</Label>
        <Input value={cfg.statuses} onChange={(e) => setCfg({ ...cfg, statuses: e.target.value })} data-testid="proj-statuses-input" />
        <div className="text-xs opacity-60 mt-1">Example: draft, planning, active, on_hold, completed, cancelled</div>
      </div>
      <div>
        <Label>Priorities (comma-separated)</Label>
        <Input value={cfg.priorities} onChange={(e) => setCfg({ ...cfg, priorities: e.target.value })} />
      </div>
      <div>
        <Label>Milestone Statuses (comma-separated)</Label>
        <Input value={cfg.milestone_statuses} onChange={(e) => setCfg({ ...cfg, milestone_statuses: e.target.value })} />
      </div>
      <Button onClick={save} disabled={saving} data-testid="proj-config-save">{saving ? "Saving…" : "Save"}</Button>
    </Card>
  );
}


function AccessControlCard() {
  const ROLES_COLS = ["hr", "sales_manager", "department_manager", "employee", "finance_team"];
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  // Strip any legacy role codes (e.g., 'finance_manager') so they never rehydrate or get re-saved.
  const sanitize = (m) => {
    const allowed = new Set(ROLES_COLS);
    const out = {};
    Object.entries(m || {}).forEach(([k, v]) => { out[k] = (v || []).filter((r) => allowed.has(r)); });
    return out;
  };
  useEffect(() => {
    api.get("/app-settings").then((r) => {
      const m = r.data?.module_access;
      setMatrix(sanitize(mergeModuleAccess(m)));
    }).catch(() => setMatrix(defaultModuleAccess()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = (moduleKey, role) => {
    setMatrix((m) => {
      const list = new Set(m[moduleKey] || []);
      if (list.has(role)) list.delete(role); else list.add(role);
      return { ...m, [moduleKey]: Array.from(list) };
    });
  };
  const setRow = (moduleKey, all) => {
    setMatrix((m) => ({ ...m, [moduleKey]: all ? [...ROLES_COLS] : [] }));
  };
  const setCol = (role, all) => {
    setMatrix((m) => {
      const next = { ...m };
      NAV_MODULES.forEach(({ key }) => {
        const list = new Set(next[key] || []);
        if (all) list.add(role); else list.delete(role);
        next[key] = Array.from(list);
      });
      return next;
    });
  };
  const resetDefaults = () => setMatrix(defaultModuleAccess());
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/app-settings", { module_access: sanitize(matrix) });
      toast.success("Access control saved. Users will see the change on next page load.");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="access-control-card">
      <SectionTitle title="Access Control Matrix" description="Choose which roles can access each module in BMS. Super Admin always has full access (row implicit). Changes take effect on next page load." />
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={resetDefaults} data-testid="access-reset-defaults">Reset to Defaults</Button>
        <Button onClick={save} disabled={saving} data-testid="access-save">{saving ? "Saving…" : "Save Access Control"}</Button>
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 min-w-[200px]">Module</th>
              {ROLES_COLS.map((r) => (
                <th key={r} className="px-2 py-2 text-center min-w-[110px]">
                  <div className="capitalize text-xs">{roleLabel(r)}</div>
                  <div className="flex justify-center gap-1 mt-0.5">
                    <button type="button" onClick={() => setCol(r, true)} className="text-[9px] opacity-60 hover:opacity-100 hover:underline">all</button>
                    <button type="button" onClick={() => setCol(r, false)} className="text-[9px] opacity-60 hover:opacity-100 hover:underline">none</button>
                  </div>
                </th>
              ))}
              <th className="px-2 py-2 text-center text-xs opacity-60">Row</th>
            </tr>
          </thead>
          <tbody>
            {NAV_MODULES.map(({ key, label }) => (
              <tr key={key} className="border-t">
                <td className="px-3 py-2 font-medium">{label}</td>
                {ROLES_COLS.map((r) => (
                  <td key={r} className="px-2 py-2 text-center">
                    <input type="checkbox"
                           data-testid={`access-${key}-${r}`}
                           className="w-4 h-4 accent-orange-500 cursor-pointer"
                           checked={(matrix[key] || []).includes(r)}
                           onChange={() => toggle(key, r)} />
                  </td>
                ))}
                <td className="px-2 py-2 text-center">
                  <div className="flex justify-center gap-1">
                    <button type="button" onClick={() => setRow(key, true)} className="text-[10px] opacity-60 hover:opacity-100 hover:underline">all</button>
                    <button type="button" onClick={() => setRow(key, false)} className="text-[10px] opacity-60 hover:opacity-100 hover:underline">none</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs opacity-60">
        Sidebar & module visibility respect this matrix live. Backend API-level enforcement (403 blocking) is coming next; for now this hides the module from unauthorized users but power users could still open a URL directly. Full backend enforcement is on the roadmap.
      </div>
    </Card>
  );
}

